import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@shared/lib';

const lift = 'hover:translate-y-[var(--hover-lift)] active:scale-[var(--press-scale)]';

/** Glass pill — shared by `outline` (login) and `glass` variants */
const glassButtonStyles = `rounded-[var(--radius-button)] border font-semibold text-landing-ink bg-[var(--glass-bg)] border-[color:var(--glass-border)] [backdrop-filter:var(--glass-filter)] shadow-[0_10px_28px_-10px_rgba(var(--btn-shadow-rgb),calc(var(--shadow-strength)*0.22)),inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.95)),inset_0_calc(-1px)_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.2))] transition-all hover:bg-[var(--glass-bg-hover)] hover:border-[color:var(--glass-border-hover)] hover:shadow-[0_14px_32px_-10px_rgba(var(--btn-shadow-rgb),calc(var(--shadow-strength)*0.28)),inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*1))] ${lift}`;

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: `rounded-[var(--radius-button)] text-button-on-accent border-0 [background:var(--btn-primary-bg)] shadow-[0_8px_24px_-6px_rgba(var(--btn-shadow-rgb),calc(var(--shadow-strength)*0.4))] hover:shadow-[0_14px_32px_-6px_rgba(var(--btn-shadow-rgb),calc(var(--shadow-strength)*0.5))] ${lift}`,
        destructive: `rounded-[var(--radius-button-alt)] bg-destructive text-destructive-foreground border border-destructive-border shadow-[0_8px_20px_-8px_rgba(var(--btn-danger-shadow-rgb),calc(var(--shadow-strength)*0.45))] hover:shadow-[0_12px_28px_-8px_rgba(var(--btn-danger-shadow-rgb),calc(var(--shadow-strength)*0.55))] ${lift}`,
        /** Same look as landing Log in (GlassButton) */
        outline: glassButtonStyles,
        secondary: `rounded-[var(--radius-button)] border font-medium text-landing-ink-secondary bg-[var(--glass-bg-soft)] border-[color:var(--glass-border-soft)] [backdrop-filter:var(--glass-filter-soft)] shadow-[0_8px_24px_-10px_rgba(var(--btn-shadow-rgb),calc(var(--shadow-strength)*0.14)),inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.85))] hover:bg-[var(--glass-bg-soft-hover)] hover:border-[color:var(--glass-border-soft-hover)] hover:text-[var(--brand-brand-primary)] hover:translate-y-[var(--hover-lift)]`,
        ghost: `rounded-[var(--radius-button-alt)] border border-transparent text-landing-ink-nav hover:bg-tone-glass/40 hover:text-[var(--brand-brand-primary)]`,
        brand: `rounded-[var(--radius-button)] border-0 text-button-on-accent [background:var(--btn-primary-bg)] shadow-[0_10px_28px_-8px_rgba(var(--btn-shadow-rgb),calc(var(--shadow-strength)*0.45))] hover:shadow-[0_18px_36px_-8px_rgba(var(--btn-shadow-rgb),calc(var(--shadow-strength)*0.55))] ${lift}`,
        glass: glassButtonStyles,
      },
      size: {
        default: 'min-h-9 px-4 py-2',
        sm: 'min-h-8 px-3 text-xs',
        lg: 'min-h-10 px-8 text-sm',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
    compoundVariants: [
      {
        variant: ['default', 'brand', 'glass', 'outline', 'secondary'],
        size: ['default', 'sm', 'lg'],
        class: 'btn-glossy',
      },
      {
        variant: ['glass', 'outline'],
        size: 'icon',
        class: 'btn-glossy btn-glass-icon',
      },
      {
        variant: 'destructive',
        size: 'icon',
        class: 'btn-glossy-destructive',
      },
    ],
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, 'aria-label': ariaLabel, children, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const shouldHaveAriaLabel =
      !ariaLabel && (!children || (typeof children === 'string' && children.trim() === ''));
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        aria-label={shouldHaveAriaLabel ? undefined : ariaLabel}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
